import { Component, Inject } from '@angular/core';
import { FormGroup, FormBuilder, FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { NavBarComponent } from 'src/app/nav-bar/nav-bar.component';
import { SampleService } from 'src/app/services/sample.service';
import { catchError, ReplaySubject, Subject, takeUntil, throwError } from 'rxjs';
import { AddNewGroupComponent } from '../../add-new-group/add-new-group.component';
import { GroupService } from 'src/app/services/group.service';
import { SampleGroupModel } from 'src/app/models/sample-group-model';
import { PlantPart } from 'src/app/models/plant-part';
import {GrowthStageModel} from "../../../models/growth-stage";
import { PinDetailsService } from 'src/app/pin-reworks/pin-services/pin-details.service';

@Component({
    selector: 'app-add-edit-soft-tissue-sample',
    templateUrl: './add-edit-soft-tissue-sample.component.html',
    styleUrls: ['./add-edit-soft-tissue-sample.component.css'],
    standalone: false
})
export class AddEditSoftTissueSampleComponent {

  softTissueSampleForm: FormGroup;
  groups = [];
  group: SampleGroupModel = null;
  plantPartsList = [];
  plantPart: PlantPart;

  /**
   * The plant part to select as soon as populatePlantPartList() has a list to search, held as
   * an id. Either the sample's own when editing or the one carried from the previous sample.
   */
  private plantPartToSelect: number = null;
  growthStages: GrowthStageModel[] = []
  pinId: number = this.data.pinId;
  userId: number = this.data.userId;
  sampleId: number = this.data.sampleId;

  filteredGrowthStage: GrowthStageModel[] = [];

  isEditSampleMode:boolean = false;
  isAddNewSapleMode:boolean = false;
  isCompleted: boolean = false;
  definitions: any[] = [];
  selectedDefinitionId: number = null;
  isOrphanedLink: boolean = false;

      /** control for the selected group */
      // public groupCtrl: FormControl<SampleGroupModel> = new FormControl<SampleGroupModel>(null);

      /** control for the MatSelect filter keyword */
      public groupFilterCtrl: FormControl<string> = new FormControl<string>('');

    /** list of group filtered by search keyword */
    public filteredSampleGroups: ReplaySubject<SampleGroupModel[]> = new ReplaySubject<SampleGroupModel[]>(1);

      /** Subject that emits when the component has been destroyed. */
      protected _onGroupDestroy = new Subject<void>();


  constructor(@Inject(MAT_DIALOG_DATA) public data: any,
      private newSampleFb: FormBuilder,
      public softTissueSampleDialog: MatDialogRef<AddEditSoftTissueSampleComponent>,
      public addGroupDialog: MatDialog,
      private sampleService: SampleService,
      private groupService: GroupService,
      public nav: NavBarComponent,
      private pinDetailsService: PinDetailsService
  ) {
    this.softTissueSampleForm = this.newSampleFb.group({
      sampleName: ['',Validators.required],
      bagNumber: new FormControl(''),
      // cropAge: new FormControl(null),
      growthStage: new FormControl(null),
      plantPart: new FormControl(null),
      note: new FormControl(''),
      pinSampleGroup: [null,Validators.required],
    });
  }
  ngOnInit(): void {
    this.nav.toggleLoading(true);

    // The mode is read first, before anything that depends on it.
    //
    // setPreviousSelection() used to run above this, so its !isEditSampleMode guard read the
    // field default instead of the dialog's data and never guarded anything: opening a sample
    // to edit filled the form from the PREVIOUS sample and only corrected itself a second later.
    if(this.data !== null){
      this.isEditSampleMode = this.data.isEditSampleMode;
      this.isAddNewSapleMode = this.data.isAddNewSapleMode;
    }

    this.growthStages = this.data.growthStages;
    this.filteredGrowthStage = this.growthStages;

    // What to select once each list arrives. The group and the plant part are chosen inside
    // the calls that fetch them, so neither can race the 1000ms timer this used to rely on:
    // on a slow connection that timer fired against empty lists and both were quietly lost.
    let groupToSelect: number = null;

    if(this.isEditSampleMode){
      this.populateEditValues();
      groupToSelect = this.idOf(this.data?.linkedGroup);
      this.plantPartToSelect = this.idOf(this.data?.plantPart);
    } else {
      groupToSelect = this.setPreviousSelection();
    }

    this.populatePlantPartList();
    this.populateGroupDropdown(groupToSelect);
    this.loadDefinitions();
  }

  /**
   * An id from either an id or the object holding one.
   *
   * Both shapes genuinely arrive here: the dialog is opened with a linked group that is
   * sometimes a number and sometimes a group, and the carried-forward sample holds objects.
   */
  private idOf(value: any): number {
    return value?.id ?? value ?? null;
  }

  /**
   * The sample being edited, written straight into the form.
   *
   * This used to sit inside a 1000ms setTimeout that was waiting for the two lookup lists.
   * When nothing had been captured yet in the session the loading overlay came down at once,
   * leaving the dialog blank and clickable for that second with Update enabled, so a quick
   * click saved the blanks over the sample.
   */
  private populateEditValues(): void {
    this.isCompleted = this.data?.isCompleted;
    this.softTissueSampleForm.controls.sampleName.setValue(this.data.sampleName);
    this.softTissueSampleForm.controls.bagNumber.setValue(this.data.bagNumber ?? '');
    this.softTissueSampleForm.controls.growthStage.setValue(this.data.pinCropGrowthStageLinkId);
    this.softTissueSampleForm.controls.note.setValue(this.data.note);
  }

       /**
   * Method that sets the intial group list that will be displayed in the group dropdown.
   */
       setInitialGroupList() {
        // Load the initial group list
        this.filteredSampleGroups.next(this.groups.slice());
        // Listen for search field value changes
        this.groupFilterCtrl.valueChanges
          .pipe(takeUntil(this._onGroupDestroy))
          .subscribe(() => {
            this.filterGroups();
          });
      }

  protected filterGroups() {
    if (!this.groups) {
      return;
    }
    // Get the search keyword
    let search = this.groupFilterCtrl.value;
    if (!search) {
      this.filteredSampleGroups.next(this.groups.slice());
      return;
    } else {
      search = search.toLowerCase();
    }

    // Filter the group based on the search keyword
    this.filteredSampleGroups.next(
      this.groups.filter(group => group.name.toLowerCase().indexOf(search) > -1)
    );
  }

  populatePlantPartList(){
    this.sampleService.getSoftTissuePlantPartList().pipe(
        catchError(error =>{
          console.error();
          return throwError(()=> error);
        })
    ).subscribe((response) =>{
      this.plantPartsList = response;

      // Selected here, where the list is known to exist.
      if(this.plantPartToSelect){
        const match = this.plantPartsList.find(p => p?.id === this.plantPartToSelect);
        if(match){
          this.plantPart = match;
          this.softTissueSampleForm.controls.plantPart.setValue(match);
        }
      }
    })
   
  }

  onGrowthStageSearch(searchString: string) {
    this.filteredGrowthStage = this.growthStages.filter(
        item => item.name.toLowerCase().includes(searchString.toLowerCase())
    );
  }

  setSelectedPlantPart(plantPart: PlantPart){

    this.plantPart = plantPart;
    this.softTissueSampleForm.controls.plantPart.setValue(plantPart);
  }

    addNewGroup(){
      const dialogRef = this.addGroupDialog.open(AddNewGroupComponent, {
        data: {
          userId: this.userId,
          tenantId: this.data?.tenantId,
        },
        width: '420px'
      });
  
      dialogRef.afterClosed().subscribe(groupId => {
        if(groupId) {
          this.populateGroupDropdown(groupId);
        }
      });
    }
  
    populateGroupDropdown(groupId?: number){
      this.groupService.getGroups(this.data?.tenantId).pipe(
          catchError(error =>{
            console.error();
            return throwError(()=> error);
          })
      ).subscribe({
        next: (result) =>{
          this.groups = result;
          if(this.groups.length > 0) {
            this.groups = this.groups.filter(g => !g.isGrid);
          }

          // Only when it is actually found: a reload that misses must not wipe a selection
          // the user has already made.
          if(groupId) {
            const group = this.groups.find(g => g.id === groupId);
            if(group){
              this.group = group;
              this.softTissueSampleForm.controls.pinSampleGroup.setValue(group);
            }
          }

          this.setInitialGroupList();
          this.nav.toggleLoading(false);
        },
        // Without this the full screen overlay never comes down and the dialog cannot be
        // used or closed.
        error: () => this.nav.toggleLoading(false)
      })
    }
  
  
      saveSample() {
        this.nav.toggleLoading(true);

          let sampleObj = {
            pinId: this.pinId,
            linkedGroup: this.softTissueSampleForm.get('pinSampleGroup').value,
            name: this.softTissueSampleForm.get('sampleName').value,
            plantPart: this.plantPart,
            pinCropGrowthStageLinkId: this.softTissueSampleForm.get('growthStage').value,
            note: this.softTissueSampleForm.get('note').value,
            bagNumber: this.softTissueSampleForm.get('bagNumber').value
          };

          this.pinDetailsService.setSoftTissueSampleDetails(sampleObj);

          this.sampleService.postSoftTissueSample({
            pinId: sampleObj?.pinId,
            pinSampleGroupId: sampleObj?.linkedGroup?.id,
            name: sampleObj?.name,
            plantPartId: sampleObj?.plantPart?.id,
            pinCropGrowthStageLinkId: sampleObj?.pinCropGrowthStageLinkId,
            note: sampleObj?.note,
            bagNumber: sampleObj?.bagNumber,
            seasonFieldDetailId: this.selectedDefinitionId
          }).pipe(
              catchError(error => {
                console.error(error);
                this.nav.toggleLoading(false);
                return throwError(() => error);
              })
          ).subscribe(() => {
            this.softTissueSampleDialog.close();
          });
      }
  
      deleteSample(){
        this.nav.toggleLoading(true);
    
        this.sampleService.deleteSampleById(this.sampleId).pipe(
            catchError(error => {
              console.error(error);
              this.nav.toggleLoading(false);
              return throwError(() => error);
            })
        ).subscribe(() => {
          this.softTissueSampleDialog.close();
          this.nav.toggleLoading(false);
        });
      }

      updateSample(){

        this.nav.toggleLoading(true);

        let sampleObj = {
          pinId: this.pinId,
          sampleId: this.sampleId,
          linkedGroup: this.softTissueSampleForm.get('pinSampleGroup').value,
          name: this.softTissueSampleForm.get('sampleName').value,
          plantPart: this.softTissueSampleForm.get('plantPart').value,
          pinCropGrowthStageLinkId: this.softTissueSampleForm.get('growthStage').value,
          note: this.softTissueSampleForm.get('note').value,
          bagNumber: this.softTissueSampleForm.get('bagNumber').value,
          isCompleted: this.isCompleted
        };

        this.sampleService.updateSoftTissueSample(sampleObj?.sampleId, {
          pinId: sampleObj?.pinId,
          pinSampleGroupId: sampleObj?.linkedGroup?.id,
          name: sampleObj?.name,
          plantPartId: sampleObj?.plantPart?.id,
          pinCropGrowthStageLinkId: sampleObj?.pinCropGrowthStageLinkId,
          note: sampleObj?.note,
          bagNumber: sampleObj?.bagNumber,
          isCompleted: sampleObj?.isCompleted
        }).pipe(
            catchError(error => {
              console.error(error);
              this.nav.toggleLoading(false);
              return throwError(() => error);
            })
        ).subscribe(() => {
          if (this.selectedDefinitionId) {
            this.sampleService.updateSampleLink(this.sampleId, this.selectedDefinitionId).subscribe(() => {
              this.softTissueSampleDialog.close();
            });
          } else {
            this.softTissueSampleDialog.close();
          }
        });

      }

    cancel(){
      this.softTissueSampleDialog.close();
    }


  /**
   * Carries the last captured sample forward, so an agent filling a pin does not retype the
   * same growth stage and plant part for every bag.
   *
   * Returns the group to select and sets plantPartToSelect, both of which are applied by the
   * calls that fetch those lists. It used to search both lists itself inside a 1000ms
   * setTimeout, racing the very calls that fill them.
   *
   * Only ever called for a new sample. The caller decides that now; the guard that used to be
   * here read isEditSampleMode before it had been assigned.
   */
  setPreviousSelection(): number {
    const previousSelectedSoftTissueDetail = this.pinDetailsService.getSoftTissueSampleDetails();
    if(!previousSelectedSoftTissueDetail){
      return null;
    }

    this.softTissueSampleForm.controls.sampleName.setValue(previousSelectedSoftTissueDetail?.name);
    this.softTissueSampleForm.controls.growthStage.setValue(previousSelectedSoftTissueDetail?.pinCropGrowthStageLinkId);
    this.softTissueSampleForm.controls.note.setValue(previousSelectedSoftTissueDetail?.note);
    // Carried forward like every other field on this form. Decided 18/10/2026, during a live
    // soil lab run: the typing saved across a bench of samples is worth more than the duplicate
    // it leaves behind. Two samples sharing a bag number sort as equal and fall through to farm,
    // field and name, so the agent is expected to change it on each new sample.
    this.softTissueSampleForm.controls.bagNumber.setValue(previousSelectedSoftTissueDetail?.bagNumber ?? '');

    this.plantPartToSelect = this.idOf(previousSelectedSoftTissueDetail?.plantPart);
    return this.idOf(previousSelectedSoftTissueDetail?.linkedGroup);
  }

    ngOnDestroy() {
      this._onGroupDestroy.next();
      this._onGroupDestroy.complete();
    }

    loadDefinitions() {
      this.sampleService.getDefinitionsForPin(this.pinId).subscribe((defs) => {
        this.definitions = defs || [];
        if (this.data?.seasonFieldDetailId) {
          this.selectedDefinitionId = this.data.seasonFieldDetailId;
          this.isOrphanedLink = !this.definitions.some(d => d.id === this.selectedDefinitionId);
        } else if (this.definitions.length === 1) {
          this.selectedDefinitionId = this.definitions[0].id;
        }
      });
    }

}
